import classes from './Reports.module.scss';
import styles from './AdminUsersReport.module.scss';

import { useState, useEffect, Fragment } from 'react';
import axios from 'axios'
import moment from 'moment'
import subDays from 'date-fns/subDays'
import { DateRangePicker, Tag, CheckPicker } from 'rsuite'
import { ToastContainer, toast } from 'react-toastify'
import Tooltip from '@mui/material/Tooltip'

import ResponsiveTable from '../Customs/ResponsiveTable/ResponsiveTable'
import CustomButton from '../Customs/CustomButton/CustomButton'
import PopUpCard from '../Cards/PopUpCard/PopUpCard'
import useWindowSize from '../../assets/hooks/useWindowSize.js'
import { TIMEZONES, TZ_GROUPS, getZoneById, buildTzQueryParam } from '../../utils/timezones'


// Per-affiliate conversions and earnings for a date range, split MAIN vs FALLBACK, in the
// chosen reporting timezone. Always shows the unauthorized-country column (anything outside the
// US and Australia) and flags MAIN conversions that fell on Saturday/Sunday in US Eastern time.
const DEFAULT_TZ_ID = 'america_new_york'

const money = (n) => '$' + Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const num = (n) => Number(n || 0).toLocaleString()
const countryList = (obj) => Object.entries(obj || {}).map(([c, n]) => `${c}: ${n}`)

const Stat = ({ label, value, sub, strong, alert }) => (
    <div className={`${styles.stat} ${strong ? styles.statStrong : ''} ${alert ? styles.statAlert : ''}`}>
        <div className={styles.statLabel}>{label}</div>
        <div className={styles.statValue}>{value}</div>
        {sub && <div className={styles.statSub}>{sub}</div>}
    </div>
)

const AdminUsersReport = () => {
    const API_URL = process.env.REACT_APP_API_URL
    let { width } = useWindowSize()

    const [tzId, setTzId] = useState(DEFAULT_TZ_ID)
    const [loading, setLoading] = useState(false)
    const [rows, setRows] = useState([])
    const [totals, setTotals] = useState(null)
    const [meta, setMeta] = useState(null)
    const [expanded, setExpanded] = useState({})
    const [selectedUsers, setSelectedUsers] = useState([])          // user_ids; empty = all
    const [sort, setSort] = useState({ key: 'total_earnings', dir: 'desc' })
    const [bd, setBd] = useState(null)              // { ref, offerName, affiliate } while the breakdown modal is open
    const [bdGroup, setBdGroup] = useState('country')
    const [bdLoading, setBdLoading] = useState(false)
    const [bdData, setBdData] = useState(null)
    const yesterday = subDays(new Date(), 1)
    const [range, setRange] = useState([yesterday, yesterday])

    const predefinedRanges = [
        { label: 'Yesterday',    value: [subDays(new Date(), 1), subDays(new Date(), 1)] },
        { label: 'Today',        value: [new Date(), new Date()] },
        { label: 'Last 7 days',  value: [subDays(new Date(), 7), subDays(new Date(), 1)] },
        { label: 'Last 14 days', value: [subDays(new Date(), 14), subDays(new Date(), 1)] },
        { label: 'Last 30 days', value: [subDays(new Date(), 30), subDays(new Date(), 1)] },
    ]

    const fetchReport = async (dates = range, zoneId = tzId) => {
        if (!dates || !dates[0] || !dates[1]) { toast.error('Date range is required.'); return }
        setLoading(true)
        const start_date = moment(dates[0]).format('YYYY-MM-DD')
        const end_date = moment(dates[1]).format('YYYY-MM-DD')
        const url = `${API_URL}/api/v1/admin-users-report?start_date=${start_date}&end_date=${end_date}${buildTzQueryParam(getZoneById(zoneId))}`
        try {
            const res = await axios.get(url, { headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token') } })
            if (res.data && res.data.status === 1) {
                setRows(res.data.rows)
                setTotals(res.data.totals)
                setMeta({ ...res.data, tz_param: buildTzQueryParam(getZoneById(zoneId)) })
                setExpanded({})
                setSelectedUsers(prev => prev.filter(id => res.data.rows.some(r => r.user_id === id)))
            } else {
                toast.error(res.data?.message || 'Failed to load report')
            }
        } catch (e) {
            toast.error('Failed to load report')
        }
        setLoading(false)
    }

    useEffect(() => {
        fetchReport([yesterday, yesterday], DEFAULT_TZ_ID)
        document.title = 'LinkVantage | Users Report'
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleSelect = (dates) => setRange(dates)
    const handleTz = (e) => setTzId(e.target.value)
    const runSearch = () => fetchReport(range, tzId)
    const pending = meta && (moment(range[0]).format('YYYY-MM-DD') !== meta.start_date
                          || moment(range[1]).format('YYYY-MM-DD') !== meta.end_date
                          || buildTzQueryParam(getZoneById(tzId)) !== meta.tz_param)
    const toggleExpand = (uid) => setExpanded(prev => ({ ...prev, [uid]: !prev[uid] }))

    const downloadCsv = (filename, head, lines) => {
        const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
        const text = [head.map(esc).join(','), ...lines.map(l => l.map(esc).join(','))].join('\n')
        const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob)
        a.download = filename
        a.click()
        URL.revokeObjectURL(a.href)
    }

    // ref breakdown modal: same grouping as the Reports page breakdown, for any affiliate's ref
    const BD_GROUPS = [['country', 'Country'], ['deviceType', 'Device Category'], ['osName', 'Operating System'], ['utm_medium', 'Sub ID']]
    const fetchBreakdown = async (ref, group) => {
        if (!meta) return
        setBdLoading(true)
        const url = `${API_URL}/api/v1/admin-breakdown-reports/${ref}?start_date=${meta.start_date}&end_date=${meta.end_date}&searchParameter=${group}${meta.tz_param}`
        try {
            const res = await axios.get(url, { headers: { Authorization: 'Bearer ' + localStorage.getItem('access_token') } })
            setBdData(res.data)
        } catch (e) {
            toast.error('Failed to load breakdown')
            setBdData(null)
        }
        setBdLoading(false)
    }
    const openBreakdown = (r, o) => { setBd({ ref: o.ref, offerName: o.name, affiliate: r.name }); setBdData(null); fetchBreakdown(o.ref, bdGroup) }
    const closeBreakdown = () => { setBd(null); setBdData(null) }
    const changeBdGroup = (e) => { setBdGroup(e.target.value); if (bd) fetchBreakdown(bd.ref, e.target.value) }
    const bdRows = (bdData?.reports || []).slice().sort((a, b) => (b.totalVisits || 0) - (a.totalVisits || 0))
    const bdTotals = bdRows.reduce((t, x) => ({ visits: t.visits + (x.totalVisits || 0), unique: t.unique + (x.totalUnique || 0),
                                                 conv: t.conv + (x.totalConverted || 0), rev: t.rev + (x.conversion_revenue || 0) }),
                                   { visits: 0, unique: 0, conv: 0, rev: 0 })

    // one affiliate: a line per ref, then a summary line
    const exportAffiliateCsv = (r) => {
        const head = ['Affiliate', 'Email', 'Ref', 'Offer', 'Type', 'Conversions', 'Payout', 'Earnings']
        const lines = r.offers.map(o => [r.name, r.email, o.ref, o.name, o.type.toUpperCase(), o.conversions, o.payout, o.earnings])
        lines.push([r.name, r.email, '', 'TOTAL MAIN', 'MAIN', r.main_conversions, '', r.main_earnings])
        lines.push([r.name, r.email, '', 'TOTAL FALLBACK', 'FALLBACK', r.fallback_conversions, '', r.fallback_earnings])
        lines.push([r.name, r.email, '', 'TOTAL', '', r.total_conversions, '', r.total_earnings])
        const safe = (r.name || r.email || r.user_id).replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '')
        downloadCsv(`users_report_${safe}_${meta?.start_date}_${meta?.end_date}_${tzId}.csv`, head, lines)
    }

    const exportCsv = () => {
        const head = ['Affiliate', 'Email', 'MAIN conversions', 'MAIN earnings', 'FALLBACK conversions', 'FALLBACK earnings',
                      'Total conversions', 'Total earnings', 'Weekend MAIN conversions', 'Weekend MAIN earnings',
                      'Unauthorized-country conversions', 'Unauthorized countries', 'Unknown-country conversions']
        const lines = visibleRows.map(r => [r.name, r.email, r.main_conversions, r.main_earnings, r.fallback_conversions, r.fallback_earnings,
                        r.total_conversions, r.total_earnings, r.weekend_main_conversions, r.weekend_main_earnings,
                        r.unauthorized_conversions, countryList(r.unauthorized_countries).join('; '),
                        r.unknown_country_conversions])
        downloadCsv(`users_report_${meta?.start_date}_${meta?.end_date}_${tzId}.csv`, head, lines)
    }

    const userOptions = rows.map(r => ({ value: r.user_id, label: r.name || r.email, email: r.email }))
    const visibleRows = (() => {
        const filtered = selectedUsers.length ? rows.filter(r => selectedUsers.includes(r.user_id)) : rows
        const dirMul = sort.dir === 'asc' ? 1 : -1
        return [...filtered].sort((a, b) => {
            const va = a[sort.key], vb = b[sort.key]
            if (typeof va === 'string' || typeof vb === 'string') return dirMul * String(va ?? '').localeCompare(String(vb ?? ''), undefined, { sensitivity: 'base' })
            return dirMul * ((Number(va) || 0) - (Number(vb) || 0))
        })
    })()
    const visibleTotals = totals && (() => {
        const t = { affiliates: visibleRows.length, main_conversions: 0, main_earnings: 0, fallback_conversions: 0, fallback_earnings: 0,
                    total_conversions: 0, total_earnings: 0, unauthorized_conversions: 0, flagged_country: 0 }
        visibleRows.forEach(r => {
            for (const k of ['main_conversions', 'main_earnings', 'fallback_conversions', 'fallback_earnings', 'total_conversions', 'total_earnings', 'unauthorized_conversions']) t[k] += Number(r[k]) || 0
            if (r.unauthorized_conversions > 0) t.flagged_country += 1
        })
        return t
    })()

    const toggleSort = (key) => setSort(prev => ({ key, dir: prev.key === key ? (prev.dir === 'asc' ? 'desc' : 'asc') : 'desc' }))
    const SortTh = ({ k, children, numeric, left }) => (
        <th className={`${styles.sortable} ${numeric ? styles.num : ''} ${left ? styles.left : ''} ${sort.key === k ? styles.sorted : ''}`} onClick={() => toggleSort(k)}
            title="Click to sort">
            {children}<span className={styles.sortIcon}>{sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ' ⇅'}</span>
        </th>
    )

    const maxTotal = Math.max(0, ...visibleRows.map(r => Number(r.total_earnings) || 0))
    const grand = visibleTotals ? visibleTotals.total_earnings : 0

    const renderRows = () => visibleRows.map((r, i) => (
        <Fragment key={r.user_id}>
            <tr className={`${styles.row} ${expanded[r.user_id] ? styles.rowOpen : ''}`}>
                <td className={styles.rankCell}>
                    <span className={styles.rank}>{i + 1}</span>
                </td>
                <td className={styles.affiliateCell} onClick={() => toggleExpand(r.user_id)}>
                    <div className={styles.affiliateInner}>
                        <button type="button" className={styles.expandBtn} title={expanded[r.user_id] ? 'Hide refs' : 'Show refs'} aria-expanded={!!expanded[r.user_id]}>
                            {expanded[r.user_id] ? '−' : '+'}
                        </button>
                        <span className={styles.affiliateText}>
                            <span className={styles.affiliate}>{r.name}</span>
                            <span className={styles.email}>{r.email} · {r.offers.length} ref{r.offers.length === 1 ? '' : 's'}</span>
                        </span>
                    </div>
                </td>
                <td className={styles.num}>{num(r.main_conversions)}</td>
                <td className={styles.num}>{money(r.main_earnings)}</td>
                <td className={styles.num}>{num(r.fallback_conversions)}</td>
                <td className={styles.num}>{money(r.fallback_earnings)}</td>
                <td className={`${styles.num} ${styles.total}`}>
                    {money(r.total_earnings)}
                    <span className={styles.share}>{grand ? Math.round(100 * r.total_earnings / grand) : 0}%</span>
                    <span className={styles.bar}><span style={{ width: `${maxTotal ? 100 * r.total_earnings / maxTotal : 0}%` }} /></span>
                </td>
                <td className={styles.actionCell}>
                    <button type="button" className={styles.rowBtn} onClick={() => exportAffiliateCsv(r)} title={`Download CSV for ${r.name}`}>
                        ⬇ CSV
                    </button>
                </td>
            </tr>
            {expanded[r.user_id] && (
                <tr className={styles.offersRow}>
                    <td></td>
                    <td colSpan={7}>
                        <table className={styles.offersTable}>
                            <thead>
                                <tr>
                                    <th>Ref</th><th>Offer</th><th>Type</th>
                                    <th className={styles.num}>Conversions</th>
                                    <th className={styles.num}>Payout</th>
                                    <th className={styles.num}>Earnings</th>
                                </tr>
                            </thead>
                            <tbody>
                                {r.offers.map(o => (
                                    <tr key={o.ref || o.offer_id}>
                                        <td>
                                            {o.ref
                                                ? <button type="button" className={styles.refBtn} onClick={() => openBreakdown(r, o)} title="Open breakdown report"><code>{o.ref}</code> ↗</button>
                                                : <code>—</code>}
                                        </td>
                                        <td>{o.name}</td>
                                        <td>{o.type === 'fallback' ? <Tag size="sm" color="violet">FALLBACK</Tag> : <Tag size="sm" color="blue">MAIN</Tag>}</td>
                                        <td className={styles.num}>{num(o.conversions)}</td>
                                        <td className={styles.num}>{money(o.payout)}</td>
                                        <td className={styles.num}>{money(o.earnings)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </td>
                </tr>
            )}
        </Fragment>
    ))

    return (
        <>
            <ToastContainer />
            <div className={classes.container}>
                <div className={classes.main}>
                    <div className={classes.main_middle}>

                        <div className={styles.header}>
                            <div className={styles.titleWrap}>
                                <div className="heading">Users Report</div>
                                <Tooltip arrow placement="right" title={
                                    <div className={styles.infoTip}>
                                        {meta && <div>Showing <b>{meta.start_date}</b> to <b>{meta.end_date}</b> in <b>{meta.tz}</b>.</div>}
                                        <div><b>FALLBACK</b> = offer name contains "fallback"; everything else is MAIN.</div>
                                        <div><b>Earnings</b> = conversions × offer payout.</div>
                                        <div>Per-affiliate country details (unauthorized = outside US and Australia) are included in the CSV export.</div>
                                        <div>Dates are read in the selected timezone. Select Saturday–Sunday in New York time to review weekend conversions.</div>
                                    </div>
                                }>
                                    <span className={styles.infoIcon} aria-label="About this report">i</span>
                                </Tooltip>
                            </div>
                            <div className={styles.controls}>
                                <CheckPicker
                                    data={userOptions}
                                    value={selectedUsers}
                                    onChange={(v) => setSelectedUsers(v || [])}
                                    searchable
                                    placeholder="All affiliates"
                                    style={{ width: 240 }}
                                    disabled={loading || rows.length === 0}
                                    renderMenuItem={(label, item) => <span>{label}<span className={styles.pickerEmail}>{item.email}</span></span>}
                                    renderValue={(value, items) => <span>{items.length === 1 ? items[0].label : `${items.length} affiliates`}</span>}
                                />
                                <DateRangePicker
                                    showOneCalendar={width < 1030}
                                    appearance="default"
                                    placement="bottomEnd"
                                    ranges={predefinedRanges}
                                    value={range}
                                    onChange={handleSelect}
                                    cleanable={false}
                                    disabled={loading}
                                />
                                <select className={styles.tzSelect} value={tzId} onChange={handleTz} disabled={loading}
                                        title="Reporting timezone for the date range">
                                    {TZ_GROUPS.map(g => (
                                        <optgroup key={g.key} label={g.heading}>
                                            {TIMEZONES.filter(z => z.group === g.key).map(z => (
                                                <option key={z.id} value={z.id}>{z.label}</option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                                <CustomButton color="blue" name={loading ? 'Searching…' : 'Search'} disabled={loading} onClick={runSearch} />
                                <CustomButton color="green" name="Export CSV" disabled={rows.length === 0 || loading} onClick={exportCsv} />
                            </div>
                        </div>

                        {pending && <p className={styles.note}><span className={styles.pending}>Date or timezone changed, press Search to reload.</span></p>}

                        {visibleTotals && (
                            <div className={styles.cards}>
                                <Stat label="Affiliates" value={num(visibleTotals.affiliates)}
                                      sub={selectedUsers.length ? `of ${num(totals.affiliates)} in range` : 'with conversions'} />
                                <Stat label="MAIN" value={money(visibleTotals.main_earnings)} sub={`${num(visibleTotals.main_conversions)} conversions`} />
                                <Stat label="FALLBACK" value={money(visibleTotals.fallback_earnings)} sub={`${num(visibleTotals.fallback_conversions)} conversions`} />
                                <Stat label="Total" value={money(visibleTotals.total_earnings)} sub={`${num(visibleTotals.total_conversions)} conversions`} strong />
                            </div>
                        )}

                        <div className={styles.tableWrap}>
                            <ResponsiveTable loadingResults={loading} design={{ overflowX: 'auto' }}>
                                <thead>
                                    <tr>
                                        <th className={styles.rankCell}>#</th>
                                        <SortTh k="name" left>Affiliate</SortTh>
                                        <SortTh k="main_conversions" numeric>MAIN</SortTh>
                                        <SortTh k="main_earnings" numeric>MAIN $</SortTh>
                                        <SortTh k="fallback_conversions" numeric>FALLBACK</SortTh>
                                        <SortTh k="fallback_earnings" numeric>FALLBACK $</SortTh>
                                        <SortTh k="total_earnings" numeric>Total $</SortTh>
                                        <th className={styles.actionCell}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {visibleRows.length === 0
                                        ? <tr><td colSpan={8} className={styles.empty}>{rows.length === 0 ? 'No conversions in this range' : 'No affiliates match the selection'}</td></tr>
                                        : renderRows()}
                                </tbody>
                                {visibleRows.length > 1 && visibleTotals && (
                                    <tfoot>
                                        <tr className={styles.footer}>
                                            <td></td>
                                            <td className={styles.left}>Total · {num(visibleTotals.affiliates)} affiliates</td>
                                            <td className={styles.num}>{num(visibleTotals.main_conversions)}</td>
                                            <td className={styles.num}>{money(visibleTotals.main_earnings)}</td>
                                            <td className={styles.num}>{num(visibleTotals.fallback_conversions)}</td>
                                            <td className={styles.num}>{money(visibleTotals.fallback_earnings)}</td>
                                            <td className={styles.num}>{money(visibleTotals.total_earnings)}</td>
                                            <td></td>
                                        </tr>
                                    </tfoot>
                                )}
                            </ResponsiveTable>
                        </div>
                    </div>
                </div>
            </div>

            {bd && (
                <PopUpCard loading={bdLoading} bigPopup={true} header={`Breakdown · ${bd.ref}`} toggle={closeBreakdown}>
                    <div className={styles.bdWrap}>
                        <div className={styles.bdHead}>
                            <div>
                                <div className={styles.bdTitle}>{bd.offerName}</div>
                                <div className={styles.bdSub}>{bd.affiliate} · {meta?.start_date} to {meta?.end_date} · {meta?.tz}</div>
                            </div>
                            <label className={styles.bdGroup}>
                                Group by
                                <select className={styles.tzSelect} value={bdGroup} onChange={changeBdGroup} disabled={bdLoading}>
                                    {BD_GROUPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            </label>
                        </div>

                        {bdData?.top_cards && (
                            <div className={styles.cards}>
                                {bdData.top_cards.cpa_params && <>
                                    <Stat label="Conversions" value={num(bdData.top_cards.cpa_params.cpa_count)} sub={`payout ${money(bdData.payout)}`} />
                                    <Stat label="Conversion revenue" value={money(bdData.top_cards.cpa_params.cpa_revenue)} />
                                </>}
                                {bdData.top_cards.content_params && <>
                                    <Stat label="Users" value={num(bdData.top_cards.content_params.content_users)} />
                                    <Stat label="Ad impressions" value={num(bdData.top_cards.content_params.content_impression)} />
                                    <Stat label="Content revenue" value={money(bdData.top_cards.content_params.content_revenue)} />
                                </>}
                                {bdData.top_cards.search_params && <>
                                    <Stat label="Search clicks" value={num(bdData.top_cards.search_params.search_clicks)} sub={`${num(bdData.top_cards.search_params.search_impressions)} impressions`} />
                                    <Stat label="Search revenue" value={money(bdData.top_cards.search_params.search_revenue)} />
                                </>}
                                <Stat label="Total revenue" value={money(bdData.top_cards.total_revenue)} strong />
                            </div>
                        )}

                        <div className={`${styles.tableWrap} ${styles.bdTable}`}>
                            <table>
                                <thead>
                                    <tr>
                                        <th className={styles.left}>{BD_GROUPS.find(g => g[0] === bdGroup)?.[1]}</th>
                                        <th className={styles.num}>Total Visits</th>
                                        <th className={styles.num}>Unique Visitors</th>
                                        <th className={styles.num}>Conversions</th>
                                        <th className={styles.num}>Conversion Rate</th>
                                        <th className={styles.num}>EPC</th>
                                        <th className={styles.num}>Total Revenue</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!bdLoading && bdRows.length === 0 && (
                                        <tr><td colSpan={7} className={styles.empty}>No click data for this ref in the selected range</td></tr>
                                    )}
                                    {bdRows.map((x, i) => (
                                        <tr key={i} className={styles.row}>
                                            <td className={styles.left}>{x._id?.dimention === '' ? '(not set)' : (x._id?.dimention ?? 'N/A')}</td>
                                            <td className={styles.num}>{num(x.totalVisits)}</td>
                                            <td className={styles.num}>{num(x.totalUnique)}</td>
                                            <td className={styles.num}>{num(x.totalConverted)}</td>
                                            <td className={styles.num}>{Number(x.conversion_rate || 0).toLocaleString()}%</td>
                                            <td className={styles.num}>{money(x.avg_epc)}</td>
                                            <td className={`${styles.num} ${styles.total}`}>{money(x.conversion_revenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                {bdRows.length > 1 && (
                                    <tfoot>
                                        <tr className={styles.footer}>
                                            <td className={styles.left}>Total</td>
                                            <td className={styles.num}>{num(bdTotals.visits)}</td>
                                            <td className={styles.num}>{num(bdTotals.unique)}</td>
                                            <td className={styles.num}>{num(bdTotals.conv)}</td>
                                            <td className={styles.num}>{bdTotals.unique ? (100 * bdTotals.conv / bdTotals.unique).toLocaleString(undefined, { maximumFractionDigits: 3 }) : 0}%</td>
                                            <td className={styles.num}>{money(bdTotals.unique ? bdTotals.rev / bdTotals.unique : 0)}</td>
                                            <td className={styles.num}>{money(bdTotals.rev)}</td>
                                        </tr>
                                    </tfoot>
                                )}
                            </table>
                        </div>
                    </div>
                </PopUpCard>
            )}
        </>
    )
}

export default AdminUsersReport;
